import { Firma, Pantalla, Seguro } from './ui.js';

/**
 * El final: verificado, no verificado, pendiente.
 *
 * ── LOS TRES COLORES NO SON DEL CLIENTE ────────────────────────────────────────
 *
 * `--acento` y `--acento-2` sí; estos no, y por eso van en hexadecimal a mano — el único
 * sitio del front donde eso es correcto. Un cliente con la marca roja no puede convertir
 * "verificado" en rojo. **El significado de un color no se subcontrata.**
 *
 * ── Y EL ROJO ES SOBRIO A PROPÓSITO ────────────────────────────────────────────
 *
 * Nada de iconos de alarma, nada de rojo de emergencia. Detrás de esta pantalla hay una
 * persona que quizá solo tenía mala luz, un DNI viejo o un teléfono malo — y la abrumadora
 * mayoría de quienes la ven son honestos. Una UI que grita FRAUDE a alguien que se equivocó de
 * encuadre es a la vez cruel e inexacta.
 *
 * ── QUÉ HACER, NUNCA POR QUÉ FALLÓ ─────────────────────────────────────────────
 *
 * Esta pantalla no dice qué se detectó. No porque sea un secreto: porque cada detalle es un
 * informe gratis para quien lo está intentando engañar —"no se detectó el parpadeo" le dice
 * exactamente qué clip le falta— y a la persona honesta no le sirve de nada. Ella no puede
 * "parpadear mejor": lo que necesita es saber a quién preguntar.
 *
 * Por eso el no verificado no lleva causa: lleva una salida.
 */

const VERDE = '#12A150';
const AMBAR = '#F5A524';
/** Sobrio, no de emergencia. Ver arriba. */
const ROJO = '#C0384A';

export type Desenlace = 'verificado' | 'no_verificado' | 'pendiente';

export function Resultado({
  desenlace,
  paraQuien,
  logoUrl,
}: {
  desenlace: Desenlace;
  /** El nombre del cliente. Es a quien la persona tiene que escribir si algo va mal. */
  paraQuien: string;
  logoUrl?: string | null;
}) {
  const v = VISTA[desenlace];

  return (
    <Pantalla>
      <header className="flex items-center justify-between px-5 pt-5">
        {logoUrl !== null && logoUrl !== undefined && logoUrl !== '' ? (
          <img src={logoUrl} alt="" className="h-7 max-w-[120px] object-contain" />
        ) : (
          <span className="text-[15px] font-bold">{paraQuien}</span>
        )}
        <Seguro texto={desenlace === 'verificado' ? 'Verificado' : 'Seguro'} />
      </header>

      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        <Icono desenlace={desenlace} />

        <h1 className="mt-7 text-[25px] font-bold leading-tight">{v.titulo}</h1>
        <p className="mt-2 max-w-[300px] text-[14px] leading-relaxed" style={{ color: 'rgba(16,17,20,.6)' }}>
          {v.texto(paraQuien)}
        </p>
      </div>

      {/*
        No hay botón. Ni "cerrar", ni "volver al inicio", ni "reintentar".
 
        Cerrar una pestaña no lo puede hacer JavaScript salvo que él mismo la abriera, así que
        un botón "Cerrar" no haría nada en el móvil de casi nadie — y un botón que no funciona
        es peor que ninguno. Y "reintentar" tampoco: quien decide si hay otro intento es el
        cliente por API, no esta pantalla. Ofrecerlo sería prometer algo que no controlamos.
      */}
      <Firma />
    </Pantalla>
  );
}

const VISTA: Record<Desenlace, { titulo: string; texto: (quien: string) => string }> = {
  verificado: {
    titulo: 'Identidad verificada',
    texto: () => 'Puedes cerrar esta ventana.',
  },
  no_verificado: {
    titulo: 'No pudimos verificarte',
    // Ni una palabra de qué falló. Lo accionable es a quién escribir — y por eso el nombre del
    // cliente entra en la frase: "contacta con quien te lo pidió" es un callejón sin salida si
    // la persona no recuerda quién fue.
    texto: (quien) =>
      `No cierres el trámite todavía: escribe a ${quien} y te dirán cómo continuar.`,
  },
  pendiente: {
    titulo: 'Verificación pendiente',
    // La revisión humana no es un fallo, y no debe parecerlo. Que alguien mire el caso es
    // exactamente lo que protege a quien se equivocó de encuadre.
    texto: (quien) =>
      `Estamos revisando tu verificación. ${quien} te avisará cuando esté lista. Puedes cerrar esta ventana.`,
  },
};

function Icono({ desenlace }: { desenlace: Desenlace }) {
  const color = desenlace === 'verificado' ? VERDE : desenlace === 'pendiente' ? AMBAR : ROJO;

  return (
    <div
      className="grid h-[76px] w-[76px] place-items-center rounded-full"
      style={{ background: color }}
      role="img"
      aria-label={VISTA[desenlace].titulo}
    >
      {desenlace === 'verificado' ? (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="m5 12.5 4.5 4.5L19 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : desenlace === 'pendiente' ? (
        // Un reloj, no una exclamación: esto no es una advertencia, es una espera.
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="8.5" stroke="#fff" strokeWidth="2" />
          <path d="M12 7.5V12l3 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        // Un guion, no una cruz ni un triángulo de peligro. "No se pudo", no "estás acusado".
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M7 12h10" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      )}
    </div>
  );
}
